// #1602 — member MCs needing the lead's review-cover attention.
// `useMcAttention` reads `GET /api/cycles/{id}/mc-attention` (lead-only,
// gated on `mc_recover`): member-raised MC rows whose dates intersect the
// cycle window AND at least one published assignment, minus ones the lead
// already handled (mc-recover applied, or dismissed). `useDismissMcAttention`
// posts an explicit dismiss — the backend persists it as an audit row.
// Auth is attached centrally by `apiFetch` (#773).
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { type ApiError, apiFetch } from "./client";
import { liveRefetchOptions } from "./liveRefetch";

// #1603 — one published roster row of the MC'd member on an affected date;
// the "Ask the team" broadcast can narrow to a subset (default all).
export interface McAttentionAffectedAssignment {
  assignment_id: string;
  date: string;
  role: string;
  /** #1999 — the ladder cell's label ("56 AM") while this seat is short on
   *  ITS OWN day; null once it is covered. (The item's `still_short` labels
   *  carry no date, so a one-day reader must not use them.) */
  still_short?: string | null;
  /** #1999 — false when the ladder cannot speak about the role at all (an
   *  on-call the config does not define, an unsolved roster): "can't
   *  tell", never "covered". Absent → true. */
  knowable?: boolean;
}

export interface McAttentionItem {
  leave_id: string;
  membership_id: string;
  display_name: string;
  /** ISO yyyy-mm-dd, ascending — the MC window clamped to the cycle window
   *  (the days the lead covers from each day's options ladder). */
  dates: string[];
  affected_assignment_count: number;
  /** #1603 — the rows behind the count (for the broadcast's subset picker). */
  affected_assignments?: McAttentionAffectedAssignment[];
  /** #1603 — still-open `origin=mc_cover` asks on this item's shifts:
   *  > 0 means the lead already asked the team (item is IN PROGRESS). */
  open_cover_asks?: number;
  /** The ladder's labels ("56 AM") for the seats this MC vacated that the
   *  call-back ladder still calls short. The two reads agree: an item whose
   *  seats are ALL covered is not served at all, so this is either the
   *  remaining gaps or empty (the ladder can't speak about those roles). */
  still_short?: string[];
  /** Always false on a served row — a covered MC is excluded server-side,
   *  exactly like a handled or dismissed one. */
  covered?: boolean;
  created_at: string;
}

/** The inbox's SECOND kind: a rest day this cycle still owes someone the
 *  lead recalled. Every door into the call-back ladder is gated on the day
 *  still being short, so a recall that closed the gap left the day it owed
 *  back unreachable — this row is the door. `recall_date` is the day the
 *  ladder opens on; `rest_date` is the day being offered back. */
export interface McAttentionRestOwed {
  membership_id: string;
  display_name: string;
  recall_date: string;
  rest_date: string;
  days_owed: number;
  released?: string[];
  note: string;
}

export interface McAttentionResponse {
  cycle_id: string;
  items: McAttentionItem[];
  /** Rest still owed after a recall. The chip counts these alongside
   *  `items`; the list renders them as their own rows. */
  rest_owed?: McAttentionRestOwed[];
}

export interface McAttentionDismissBody {
  membership_id: string;
  dates: string[];
}

export interface McAttentionDismissResponse {
  cycle_id: string;
  membership_id: string;
  dates: string[];
  already_dismissed: boolean;
}

export const mcAttentionQueryKey = (cycleId: string) => ["mc-attention", cycleId] as const;

export function useMcAttention(cycleId: string, enabled: boolean) {
  return useQuery<McAttentionResponse>({
    queryKey: mcAttentionQueryKey(cycleId),
    enabled: Boolean(enabled && cycleId),
    staleTime: 30_000,
    retry: false,
    // #90 — the chip IS the signal that a member just reported off sick;
    // it has to appear in an open lead window unaided. See `liveRefetch.ts`.
    ...liveRefetchOptions,
    queryFn: () => apiFetch<McAttentionResponse>(`/api/cycles/${cycleId}/mc-attention`),
  });
}

// ─── #1603 — "Ask the team" broadcast ────────────────────────────────

export interface McCoverBroadcastBody {
  mc_membership_id: string;
  dates: string[];
  assignment_ids?: string[];
}

export interface McCoverAskCreated {
  swap_id: string;
  assignment_id: string;
  date: string;
  role: string;
  eligible_pool_size: number;
}

export interface McCoverAskSkipped {
  assignment_id: string;
  date: string | null;
  role: string | null;
  reason: string;
}

export interface McCoverBroadcastResponse {
  cycle_id: string;
  created: McCoverAskCreated[];
  skipped: McCoverAskSkipped[];
}

/** #1603 — lead broadcast: create `origin=mc_cover` swap asks (on behalf
 *  of the sick member) for their published shifts on the MC dates. The
 *  attention list + swaps board re-read so the item flips to "in
 *  progress" and the new asks appear with their "Cover ask" badge — and so
 *  do the call-back ladder's reads, whose cells say "Cover ask out · N":
 *  sent from Review cover or from the ladder's own empty cell, the ask shows
 *  on the cell the next time the day opens (the key is
 *  `shortfallOptionsQueryKey`'s; importing it would loop the two modules). */
export function useMcCoverBroadcast(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<McCoverBroadcastResponse, ApiError, McCoverBroadcastBody>({
    mutationFn: (body) =>
      apiFetch<McCoverBroadcastResponse>(`/api/cycles/${cycleId}/mc-cover/broadcast`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mcAttentionQueryKey(cycleId) });
      qc.invalidateQueries({ queryKey: ["cycle-shortfall-options", cycleId] });
      qc.invalidateQueries({ queryKey: ["cycle-swaps"] });
      qc.invalidateQueries({ queryKey: ["me-swaps-incoming"] });
      qc.invalidateQueries({ queryKey: ["me-swaps-outgoing"] });
    },
  });
}

export function useDismissMcAttention(cycleId: string) {
  const qc = useQueryClient();
  return useMutation<McAttentionDismissResponse, ApiError, McAttentionDismissBody>({
    mutationFn: (body) =>
      apiFetch<McAttentionDismissResponse>(`/api/cycles/${cycleId}/mc-attention/dismiss`, {
        method: "POST",
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: mcAttentionQueryKey(cycleId) });
    },
  });
}
